/**
 * FIDE federation code -> flag emoji: the KMP port shared by the player page
 * (`profile.ts` re-exports it) and the live round page's top-games rail.
 *
 * Its own module so the round page's chunk does not carry the profile
 * derivation layer and its protobuf codegen for one function. Pure, no imports.
 * Agreement test: `src/test/players.profile.test.ts`.
 */

/**
 * FIDE federation code (e.g. "NOR") → flag emoji, via ISO 3166-1 alpha-2 regional
 * indicators; null for an unknown, absent or malformed code.
 *
 * Copy of the canonical table — the web cannot import Kotlin. The agreement test
 * `federationFlag agrees with the KMP table` reads
 * `shared/src/commonMain/kotlin/com/chessstreamlive/model/FederationFlags.kt`
 * itself and compares every entry, so this table cannot drift from the app's in
 * silence: a player must never fly different flags on the two clients.
 */
export const FIDE_TO_ISO: Readonly<Record<string, string>> = { // mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/model/FederationFlags.kt
  USA: "US", RUS: "RU", CHN: "CN", IND: "IN", FRA: "FR",
  GER: "DE", ENG: "GB", ESP: "ES", NOR: "NO", NED: "NL",
  POL: "PL", HUN: "HU", CZE: "CZ", ARM: "AM", AZE: "AZ",
  // Iran is carried BOTH ways: FIDE's rating lists publish the IOC-style "IRI",
  // while ISO 3166-1 alpha-3 says "IRN". Both are unambiguously Iran, so both
  // map — a player must not lose their flag to a spelling their source happens to use.
  GEO: "GE", UKR: "UA", ISR: "IL", IRN: "IR", IRI: "IR",
  UZB: "UZ", UAE: "AE", GBR: "GB", TKM: "TM", KGZ: "KG",
  TJK: "TJ",
  CRO: "HR", SRB: "RS", ROU: "RO", BUL: "BG", GRE: "GR",
  ITA: "IT", SWE: "SE", DEN: "DK", FIN: "FI", AUT: "AT",
  SUI: "CH", BEL: "BE", POR: "PT", ARG: "AR", BRA: "BR",
  CUB: "CU", PER: "PE", COL: "CO", PHI: "PH", VIE: "VN",
  INA: "ID", MGL: "MN", KAZ: "KZ", TUR: "TR", SCO: "GB",
  IRL: "IE", CAN: "CA", AUS: "AU", NZL: "NZ", RSA: "ZA",
  EGY: "EG", TUN: "TN", ALG: "DZ", MAR: "MA", NGR: "NG",
  KOR: "KR", JPN: "JP", SGP: "SG", MAS: "MY", THA: "TH",
  LAT: "LV", LTU: "LT", EST: "EE", BLR: "BY", MDA: "MD",
  SVK: "SK", SLO: "SI", BIH: "BA", MKD: "MK", MNE: "ME",
  ALB: "AL", KOS: "XK", AND: "AD", ISL: "IS", FAI: "FO",
  LUX: "LU", MON: "MC", LIE: "LI", MLT: "MT", CYP: "CY",
  WAL: "GB", PAK: "PK", BAN: "BD", SRI: "LK", NEP: "NP",
  MEX: "MX", CHI: "CL", PAR: "PY", URU: "UY", ECU: "EC",
  BOL: "BO", VEN: "VE", DOM: "DO", PUR: "PR", CRC: "CR",
  GUA: "GT", PAN: "PA", JAM: "JM", TTO: "TT", BAR: "BB",
};

const REGIONAL_INDICATOR_A = 0x1f1e6;

/**
 * The profile page's federation flag, or null for "no flag".
 *
 * Port of KMP `federationFlag`, which is itself a null-shaped adapter over
 * `model/FederationFlags.emoji` — the same two-step, and the same refusal to
 * render garbage scalars: an unknown code, a bare FIDE id or a club name yields
 * null (the KMP mapper's `""`), never a partial pair of regional indicators.
 */
export function federationFlag(code: string | null | undefined): string | null { // mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/PlayerProfilePresentation.kt
  const trimmed = (code ?? "").trim().toUpperCase();
  if (trimmed.length === 0 || !/^[A-Z]+$/.test(trimmed)) return null;
  const iso2 = trimmed.length === 2 ? trimmed : trimmed.length === 3 ? FIDE_TO_ISO[trimmed] : undefined;
  if (iso2 === undefined) return null;
  return [...iso2].map((c) => String.fromCodePoint(REGIONAL_INDICATOR_A + (c.codePointAt(0) ?? 0) - 65)).join("");
}

/**
 * Team or country NAME → [FIDE federation code, ISO 3166-1 alpha-2], keyed by
 * the folded name (see `foldName`).
 *
 * Why it exists: Olympiad feeds publish team names in `PlayerCard.federation`
 * ("United States of America", "Bosnia & Herzegovina", "Uzbekistan 3") — read
 * off arrowchess.com rounds HnCuRMmB and SnyTUPmk on 2026-09-17 — and
 * `federationFlag` (the KMP mirror) rightly answers null for a name. Neither the
 * app nor the backend carries a name table (searched `shared/`, `iosApp/`,
 * `relay-server-rs/`: `parse_federation` accepts codes only), so this is the one
 * definition, web-side.
 *
 * The FIDE codes are the ones the 2024 Olympiad's own players carry per team
 * (`data-curated/standings-goldens/olympiad-2024/open-lichess-players.json`),
 * which is why Wales reads `WLS` and Monaco `MNC` here. The ISO column is what
 * lets a code the KMP table does not carry (El Salvador `ESA`) still fly a flag;
 * where the KMP table DOES carry the code the two must agree — England,
 * Scotland and Wales fly GB exactly as they do in the app. Agreement tests:
 * `src/test/federation-names.test.ts` (against both sources).
 */
export const FEDERATION_NAMES: Readonly<Record<string, readonly [string, string]>> = {
  "afghanistan": ["AFG", "AF"], "albania": ["ALB", "AL"], "algeria": ["ALG", "DZ"], "andorra": ["AND", "AD"],
  "angola": ["ANG", "AO"], "antigua and barbuda": ["ANT", "AG"], "argentina": ["ARG", "AR"], "armenia": ["ARM", "AM"],
  "aruba": ["ARU", "AW"], "australia": ["AUS", "AU"], "austria": ["AUT", "AT"], "azerbaijan": ["AZE", "AZ"],
  "bahamas": ["BAH", "BS"], "bahrain": ["BRN", "BH"], "bangladesh": ["BAN", "BD"], "barbados": ["BAR", "BB"],
  "belarus": ["BLR", "BY"], "belgium": ["BEL", "BE"], "belize": ["BIZ", "BZ"], "bermuda": ["BER", "BM"],
  "bhutan": ["BHU", "BT"], "bolivia": ["BOL", "BO"], "bosnia and herzegovina": ["BIH", "BA"], "botswana": ["BOT", "BW"],
  "brazil": ["BRA", "BR"], "british virgin islands": ["IVB", "VG"], "brunei darussalam": ["BRU", "BN"], "brunei": ["BRU", "BN"],
  "bulgaria": ["BUL", "BG"], "burkina faso": ["BUR", "BF"], "burundi": ["BDI", "BI"], "cambodia": ["CAM", "KH"], "cameroon": ["CMR", "CM"],
  "canada": ["CAN", "CA"], "cape verde": ["CPV", "CV"], "cayman islands": ["CAY", "KY"],
  "central african republic": ["CAF", "CF"], "chad": ["CHA", "TD"],
  "chile": ["CHI", "CL"], "china": ["CHN", "CN"], "chinese taipei": ["TPE", "TW"], "colombia": ["COL", "CO"],
  "comoros islands": ["COM", "KM"], "comoros": ["COM", "KM"], "costa rica": ["CRC", "CR"], "cote d'ivoire": ["CIV", "CI"],
  "croatia": ["CRO", "HR"], "curacao": ["CUR", "CW"], "cuba": ["CUB", "CU"], "cyprus": ["CYP", "CY"], "czech republic": ["CZE", "CZ"], "czechia": ["CZE", "CZ"],
  "democratic republic of the congo": ["COD", "CD"], "denmark": ["DEN", "DK"], "djibouti": ["DJI", "DJ"], "dominica": ["DMA", "DM"],
  "dominican republic": ["DOM", "DO"], "ecuador": ["ECU", "EC"], "egypt": ["EGY", "EG"], "el salvador": ["ESA", "SV"],
  "england": ["ENG", "GB"], "equatorial guinea": ["GEQ", "GQ"], "eritrea": ["ERI", "ER"], "estonia": ["EST", "EE"],
  "eswatini": ["SWZ", "SZ"], "ethiopia": ["ETH", "ET"], "faroe islands": ["FAI", "FO"], "fiji": ["FIJ", "FJ"],
  "finland": ["FIN", "FI"], "france": ["FRA", "FR"], "gabon": ["GAB", "GA"], "georgia": ["GEO", "GE"],
  "germany": ["GER", "DE"], "ghana": ["GHA", "GH"], "greece": ["GRE", "GR"], "grenada": ["GRN", "GD"],
  "greenland": ["GRL", "GL"], "guam": ["GUM", "GU"], "guatemala": ["GUA", "GT"], "guernsey": ["GCI", "GG"],
  "guinea": ["GUI", "GN"], "gambia": ["GAM", "GM"], "guyana": ["GUY", "GY"],
  "haiti": ["HAI", "HT"], "honduras": ["HON", "HN"], "hong kong": ["HKG", "HK"], "hong kong, china": ["HKG", "HK"],
  "hungary": ["HUN", "HU"], "iceland": ["ISL", "IS"], "india": ["IND", "IN"], "indonesia": ["INA", "ID"],
  "iran": ["IRI", "IR"], "iraq": ["IRQ", "IQ"], "ireland": ["IRL", "IE"], "isle of man": ["IOM", "IM"],
  "israel": ["ISR", "IL"], "italy": ["ITA", "IT"], "jamaica": ["JAM", "JM"], "japan": ["JPN", "JP"],
  "jersey": ["JCI", "JE"], "jordan": ["JOR", "JO"], "kazakhstan": ["KAZ", "KZ"], "kenya": ["KEN", "KE"],
  "kiribati": ["KIR", "KI"], "kosovo": ["KOS", "XK"], "kuwait": ["KUW", "KW"], "kyrgyzstan": ["KGZ", "KG"], "laos": ["LAO", "LA"],
  "latvia": ["LAT", "LV"], "lebanon": ["LBN", "LB"], "lesotho": ["LES", "LS"], "liberia": ["LBR", "LR"],
  "libya": ["LBA", "LY"], "liechtenstein": ["LIE", "LI"], "lithuania": ["LTU", "LT"], "luxembourg": ["LUX", "LU"],
  "macau": ["MAC", "MO"], "macau, china": ["MAC", "MO"], "madagascar": ["MAD", "MG"], "malawi": ["MAW", "MW"],
  "malaysia": ["MAS", "MY"], "maldives": ["MDV", "MV"], "mali": ["MLI", "ML"], "malta": ["MLT", "MT"],
  "marshall islands": ["MHL", "MH"], "mauritania": ["MTN", "MR"],
  "mauritius": ["MRI", "MU"], "mexico": ["MEX", "MX"], "moldova": ["MDA", "MD"], "monaco": ["MNC", "MC"],
  "mongolia": ["MGL", "MN"], "montenegro": ["MNE", "ME"], "morocco": ["MAR", "MA"], "mozambique": ["MOZ", "MZ"],
  "myanmar": ["MYA", "MM"], "namibia": ["NAM", "NA"], "netherlands": ["NED", "NL"], "netherlands antilles": ["CUR", "CW"],
  "nauru": ["NRU", "NR"], "nepal": ["NEP", "NP"], "new caledonia": ["NCL", "NC"],
  "new zealand": ["NZL", "NZ"], "nicaragua": ["NCA", "NI"], "niger": ["NIG", "NE"], "nigeria": ["NGR", "NG"],
  "north macedonia": ["MKD", "MK"], "norway": ["NOR", "NO"], "oman": ["OMA", "OM"], "pakistan": ["PAK", "PK"],
  "palau": ["PLW", "PW"], "palestine": ["PLE", "PS"], "panama": ["PAN", "PA"], "papua new guinea": ["PNG", "PG"],
  "paraguay": ["PAR", "PY"], "peru": ["PER", "PE"], "philippines": ["PHI", "PH"], "poland": ["POL", "PL"],
  "portugal": ["POR", "PT"], "puerto rico": ["PUR", "PR"], "qatar": ["QAT", "QA"], "romania": ["ROU", "RO"],
  "russia": ["RUS", "RU"], "saint kitts and nevis": ["SKN", "KN"], "saint lucia": ["LCA", "LC"], "saint vincent and the grenadines": ["VIN", "VC"], "san marino": ["SMR", "SM"],
  "sao tome and principe": ["STP", "ST"], "saudi arabia": ["KSA", "SA"], "scotland": ["SCO", "GB"], "senegal": ["SEN", "SN"],
  "serbia": ["SRB", "RS"], "seychelles": ["SEY", "SC"], "sierra leone": ["SLE", "SL"], "singapore": ["SGP", "SG"],
  "slovakia": ["SVK", "SK"], "slovenia": ["SLO", "SI"], "solomon islands": ["SOL", "SB"], "somalia": ["SOM", "SO"],
  "south africa": ["RSA", "ZA"], "south korea": ["KOR", "KR"], "south sudan": ["SSD", "SS"],
  "spain": ["ESP", "ES"], "sri lanka": ["SRI", "LK"], "sudan": ["SUD", "SD"], "suriname": ["SUR", "SR"],
  "sweden": ["SWE", "SE"], "switzerland": ["SUI", "CH"], "syria": ["SYR", "SY"], "tajikistan": ["TJK", "TJ"],
  "tanzania": ["TAN", "TZ"], "thailand": ["THA", "TH"], "timor-leste": ["TLS", "TL"], "togo": ["TOG", "TG"],
  "tonga": ["TGA", "TO"], "trinidad and tobago": ["TTO", "TT"],
  "tunisia": ["TUN", "TN"], "turkiye": ["TUR", "TR"], "turkey": ["TUR", "TR"], "turkmenistan": ["TKM", "TM"],
  "uganda": ["UGA", "UG"], "ukraine": ["UKR", "UA"], "united arab emirates": ["UAE", "AE"], "united states of america": ["USA", "US"],
  "united states": ["USA", "US"], "uruguay": ["URU", "UY"], "us virgin islands": ["ISV", "VI"], "uzbekistan": ["UZB", "UZ"],
  "yemen": ["YEM", "YE"],
  "vanuatu": ["VAN", "VU"], "venezuela": ["VEN", "VE"], "vietnam": ["VIE", "VN"], "wales": ["WLS", "GB"],
  "zambia": ["ZAM", "ZM"], "zimbabwe": ["ZIM", "ZW"],
};

/** FIDE code → ISO alpha-2, from {@link FEDERATION_NAMES}' own rows. */
const ISO_OF_CODE: ReadonlyMap<string, string> = new Map(Object.values(FEDERATION_NAMES).map(([code, iso]) => [code, iso]));

/** Letters NFD does not decompose, spelled the way a reader types them. */
const UNDECOMPOSED: Readonly<Record<string, string>> = { "ł": "l", "ø": "o", "đ": "d", "ı": "i", "æ": "ae", "œ": "oe", "ß": "ss", "þ": "th" };

/**
 * Case- and diacritic-insensitive text: "Çağlar, Łukasz" → "caglar, lukasz".
 * The one fold for names on the round page — the name table below keys by it
 * and the game picker's search matches with it.
 */
export function foldText(raw: string): string {
  return raw.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
    .replace(/[łøđıæœßþ]/g, (c) => UNDECOMPOSED[c] ?? c).replace(/\s+/g, " ").trim();
}

/** {@link foldText}, plus `&` read as "and" and curly apostrophes straightened. */
function foldName(raw: string): string {
  return foldText(raw.replace(/[’‘`´]/g, "'").replace(/&/g, " and "));
}

function nameRow(raw: string): readonly [string, string] | undefined {
  const folded = foldName(raw);
  const exact = FEDERATION_NAMES[folded];
  if (exact !== undefined) return exact;
  // A nation's second and third teams ("Uzbekistan 3", "Hungary B") are still that nation.
  const suffix = /^(.+) (?:\d{1,2}|[a-z])$/.exec(folded);
  return suffix?.[1] === undefined ? undefined : FEDERATION_NAMES[suffix[1]];
}

/**
 * The FIDE federation code behind a published federation: a three-letter code
 * passes through upper-cased, a known team/country name resolves through
 * {@link FEDERATION_NAMES}; anything else (a club, a blank) is null.
 */
export function federationCode(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? "").trim();
  if (trimmed.length === 0) return null;
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return nameRow(trimmed)?.[0] ?? null;
}

/** {@link federationFlag} for a code, else the flag of a known team/country name; null for no flag. */
export function teamFlag(raw: string | null | undefined): string | null {
  const byCode = federationFlag(raw);
  if (byCode !== null) return byCode;
  const trimmed = (raw ?? "").trim();
  if (trimmed.length === 0) return null;
  // A FIDE code the KMP table does not carry ("GAM") still flies its flag, through the name table's ISO column.
  if (/^[A-Z]{3}$/.test(trimmed)) return federationFlag(ISO_OF_CODE.get(trimmed));
  if (/^[A-Za-z]{2,3}$/.test(trimmed)) return null;
  const row = nameRow(trimmed);
  return row === undefined ? null : federationFlag(row[1]);
}
